import { useState, useRef, useCallback } from 'react';
import { nanoid } from 'nanoid';
import type { ConversationItem } from '../../types/conversationItem.types';
import { useTranslate } from '../../lib/i18n';
import { useCreateConversation } from '../mutations/useCreateConversation';

export const useConversation = () => {
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [conversationCreatedAt, setConversationCreatedAt] = useState<Date | null>(null);
  const [conversation, setConversation] = useState<ConversationItem[]>([]);

  const t = useTranslate();
  const initializationAttempted = useRef(false);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const { mutateAsync: createConversationMutation, isPending: isCreatingConversation } =
    useCreateConversation();

  const scrollToBottom = useCallback(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTo({
        top: chatContainerRef.current.scrollHeight,
        behavior: 'smooth',
      });
    }

    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, []);

  const initializeConversation = useCallback(() => {
    if (initializationAttempted.current) return;

    initializationAttempted.current = true;

    const welcomeMessage: ConversationItem = {
      id: nanoid(),
      author: 'SYSTEM',
      content: t('operatorChatbot.welcomeMessage'),
      time: new Date().toUTCString(),
      options: [],
    };

    setConversation([welcomeMessage]);
    scrollToBottom();
  }, [t, scrollToBottom]);

  const startConversation = useCallback(
    async (selectedPersonaGroups: string[]): Promise<string> => {
      const createdAt = new Date();
      const response = await createConversationMutation({
        type: 'OPERATOR',
        selectedPersonaGroups,
      });

      setConversationId(response.id);
      setConversationCreatedAt(createdAt);

      if (response.messages.length > 0) {
        setConversation((prev) => [...prev, ...response.messages]);
      }

      scrollToBottom();
      return response.id;
    },
    [createConversationMutation, scrollToBottom]
  );

  const updateConversation = useCallback(
    (newMessages: ConversationItem | ConversationItem[]) => {
      if (Array.isArray(newMessages)) {
        setConversation((prev) => [...prev, ...newMessages]);
      } else {
        setConversation((prev) => [...prev, newMessages]);
      }

      setTimeout(scrollToBottom, 100);
    },
    [scrollToBottom]
  );

  const removeMessages = useCallback((messageIds: string[]) => {
    setConversation((prev) => prev.filter((msg) => !messageIds.includes(msg.id)));
  }, []);

  const clearAllOptions = useCallback(() => {
    setConversation((prev) =>
      prev.map((msg) => ({
        ...msg,
        options: [],
      }))
    );
  }, []);

  const resetConversation = useCallback(() => {
    initializationAttempted.current = false;
    setConversationId(null);
    setConversationCreatedAt(null);
    setConversation([]);
    initializeConversation();
  }, [initializeConversation]);

  return {
    conversationId,
    conversationCreatedAt,
    conversation,
    isInitializing: false,
    isCreatingConversation,
    isSuccess: true,
    isError: false,
    initializeConversation,
    startConversation,
    updateConversation,
    removeMessages,
    clearAllOptions,
    resetConversation,
    chatContainerRef,
    messagesEndRef,
    scrollToBottom,
  };
};
